import { useState } from "react";
import { vehicleService } from "../../../../../../Services/vehicle.service";
import { toDateTimeISOStringFormat } from "../../../../../../Services/string.service";
import { usePermissions } from "../../../../../../contexts/PermissionsContext";

export const useVehicleActions = ({ vehicleSelected, onToast }) => {
  const { canExecuteCommand } = usePermissions();

  const [localUnderMaintenance, setLocalUnderMaintenance] = useState(
    vehicleSelected?.underMaintenace ?? "N",
  );
  const [startMaintenanceDate, setStartMaintenanceDate] = useState(null);
  const [endMaintenanceDate, setEndMaintenanceDate] = useState(null);
  const [maintenanceMessage, setMaintenanceMessage] = useState(
    vehicleSelected?.underMaintenace === "S" ? "Veículo em manutenção" : "",
  );

  const [availableCommands, setAvailableCommands] = useState([]);
  const [commandToSend, setCommandToSend] = useState(null);
  const [selectedVehicleName, setSelectedVehicleName] = useState("");

  // Modais
  const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);
  const [showCommandsModal, setShowCommandsModal] = useState(false);
  const [showAvailableCommandsModal, setShowAvailableCommandsModal] =
    useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const formatDate = (date) =>
    date ? toDateTimeISOStringFormat(new Date(date)).replace("Z", "") : null;

  const vehicleMaintenance = async (vehicleId) => {
    try {
      if (localUnderMaintenance === "N") {
        const payload = {
          startDate: formatDate(startMaintenanceDate),
          EndDate: formatDate(endMaintenanceDate),
        };
        const ok = await vehicleService.ChangeVehicleMaintenace(
          vehicleId,
          payload,
        );
        if (ok) {
          onToast({
            show: true,
            title: "Sucesso!",
            body: `Iniciado manutenção do veículo ${vehicleSelected.plate}`,
            color: "Success",
          });
        }
        setLocalUnderMaintenance("S");
        setMaintenanceMessage("Veículo em manutenção");
      } else {
        const ok = await vehicleService.DeleteVehicleMaintenace(vehicleId, {
          startDate: null,
          EndDate: null,
        });
        if (ok) {
          onToast({
            show: true,
            title: "Atenção!",
            body: `Veículo ${vehicleSelected.plate} retirado da manutenção`,
            color: "Warning",
          });
        }
        setLocalUnderMaintenance("N");
        setMaintenanceMessage("");
      }
    } catch (error) {
      console.error(error);
    } finally {
      setShowMaintenanceModal(false);
    }
  };

  const handleStartDateChange = (date) => {
    setStartMaintenanceDate(date);
    if (
      endMaintenanceDate &&
      date &&
      new Date(endMaintenanceDate) < new Date(date)
    ) {
      setEndMaintenanceDate(null);
    }
  };

  const handleEndDateChange = (date) => {
    if (
      date &&
      startMaintenanceDate &&
      new Date(date) < new Date(startMaintenanceDate)
    ) {
      onToast({
        show: true,
        title: "Atenção!",
        body: "Data final não pode ser menor que a data inicial!",
        color: "Danger",
      });
      setEndMaintenanceDate(null);
      return;
    }
    setEndMaintenanceDate(date);
  };

  // Abrir modal com a lista de comandos disponíveis é uma ação de
  // visualização — continua liberado independentemente da permissão.
  const handleShowCommands = async () => {
    try {
      const result = await vehicleService.fetchVehicleCommands(
        vehicleSelected.vehicleId,
      );
      setAvailableCommands(result);
      setShowAvailableCommandsModal(true);
    } catch (error) {
      console.error(error);
    }
  };

  // Abrir o modal de confirmação já é o passo anterior ao disparo real.
  // Se o usuário não tem permissão, nem deixamos abrir o modal de
  // confirmação (o botão que chama isso já deve estar oculto na UI,
  // mas mantemos a guarda aqui também).
  const handleOpenConfirmModal = (command) => {
    if (!canExecuteCommand) return;
    setCommandToSend(command);
    setSelectedVehicleName(vehicleSelected?.plate || "Veículo desconhecido");
    setShowConfirmModal(true);
  };

  const sendVehicleCommand = async (command) => {
    // Guarda defensiva: nunca dispara comando sem permissão, mesmo que
    // a chamada chegue até aqui por algum outro caminho.
    if (!canExecuteCommand) return;

    try {
      const payload = {
        vehicleId: vehicleSelected.vehicleId,
        commandId: command.commandId,
        parameters: null,
      };
      const response = await vehicleService.sendCommand(payload);

      onToast({
        show: true,
        title: response.error ? "Falha ao enviar comando" : "Comando enviado",
        body: command.descriptionCommand,
        color: response.error ? "danger" : "primary",
      });
    } catch (error) {
      console.error(error);
      onToast({
        show: true,
        title: "Falha ao enviar comando",
        body: command.descriptionCommand,
        color: "danger",
      });
    }
  };

  const handleConfirmSend = () => {
    if (commandToSend) sendVehicleCommand(commandToSend);
    setShowConfirmModal(false);
  };

  return {
    // Permissão
    canExecuteCommand,
    // Manutenção
    localUnderMaintenance,
    setLocalUnderMaintenance,
    startMaintenanceDate,
    endMaintenanceDate,
    maintenanceMessage,
    handleStartDateChange,
    handleEndDateChange,
    vehicleMaintenance,
    // Modais
    showMaintenanceModal,
    setShowMaintenanceModal,
    showCommandsModal,
    setShowCommandsModal,
    showAvailableCommandsModal,
    setShowAvailableCommandsModal,
    showConfirmModal,
    setShowConfirmModal,
    // Comandos
    availableCommands,
    commandToSend,
    selectedVehicleName,
    handleShowCommands,
    handleOpenConfirmModal,
    handleConfirmSend,
  };
};
